/**
 * Attachments, per ADR-023: upload once, link to a target, read the target's links.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE PATH THIS MODULE DOES NOT CONTAIN
 *   There is no presigned URL and no direct-to-storage path. The upload goes through
 *   the gated API like every other write, which is the only route the edge exposes,
 *   and the download is the API's own endpoint. A client-side bypass is not something
 *   to avoid; it is something the surface makes impossible.
 */

import { apiJson, apiUpload, num, optStr, query, str } from "./client";
import { parseClassification, type Classification } from "./credentials";

export type AttachmentTargetEntity =
  | "VERIFICATION"
  | "VERIFICATION_LINE"
  | "PUNCH_LIST_ITEM"
  | "FINDING"
  | "FINDING_RESPONSE"
  | "ACTION_REQUEST"
  | "ACTION_EVIDENCE"
  | "ITP_ITEM"
  | "ITP_REVIEW_MOVE"
  | "SCHEDULE_ACTIVITY"
  | "PROJECT"
  | "CONTRACT"
  | "PARTY";

export type AttachmentLinkPurpose =
  "EVIDENCE" | "REPORT" | "SPECIFICATION" | "DISPOSITION" | "VERIFICATION" | "GENERAL";

export interface TargetAttachment {
  linkId: string;
  linkPurpose: string;
  attachmentId: string;
  filename: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedAt: string;
  /** Who linked the file (its uploader, for a file a write linked itself). */
  linkedBy?: string | null;
  /** Wave CRED ruling 19: the file's EN 10204 classification, if it has one. */
  classification?: Classification | null;
}

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

function targetAttachment(body: Record<string, unknown>): TargetAttachment {
  return {
    linkId: str(body.link_id),
    linkPurpose: str(body.link_purpose),
    attachmentId: str(body.attachment_id),
    filename: str(body.filename),
    mimeType: str(body.mime_type),
    fileSizeBytes: num(body.file_size_bytes),
    uploadedAt: str(body.uploaded_at),
    linkedBy: optStr(body.linked_by),
    classification: parseClassification(body.classification),
  };
}

/**
 * Upload the bytes only (wave INST ruling 3): the id is then sent in a write that links
 * it itself, as the RFI declaration's evidence does at submit.
 */
export async function uploadAttachment(token: string, file: File): Promise<string> {
  const uploaded = await apiUpload(token, "/attachments/upload", file, (raw) => {
    const body = assertRecord(raw);
    return { id: str(body.id) };
  });
  return uploaded.id;
}

/** Upload the bytes, then link them to their target. Two calls, one intention. */
export async function attachToTarget(
  token: string,
  file: File,
  target: { entityType: AttachmentTargetEntity; entityId: string; purpose?: AttachmentLinkPurpose },
): Promise<{ attachmentId: string; linkId: string }> {
  const uploaded = await apiUpload(token, "/attachments/upload", file, (raw) => {
    const body = assertRecord(raw);
    return { id: str(body.id) };
  });
  const link = await apiJson(
    token,
    "/attachments/link",
    (raw) => {
      const body = assertRecord(raw);
      return { id: str(body.id) };
    },
    {
      method: "POST",
      body: {
        attachment_id: uploaded.id,
        target_entity_type: target.entityType,
        target_entity_id: target.entityId,
        link_purpose: target.purpose ?? "EVIDENCE",
      },
    },
  );
  return { attachmentId: uploaded.id, linkId: link.id };
}

export async function listTargetAttachments(
  token: string,
  target: { entityType: AttachmentTargetEntity; entityId: string },
  signal?: AbortSignal,
): Promise<TargetAttachment[]> {
  return apiJson(
    token,
    `/attachments${query({ target_type: target.entityType, target_id: target.entityId })}`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of attachments");
      }
      return raw.map((entry) => targetAttachment(assertRecord(entry)));
    },
    { signal },
  );
}

/** Remove a link. The bytes stay; only the association goes. */
export async function unlinkAttachment(token: string, linkId: string): Promise<void> {
  await apiJson(token, `/attachments/links/${linkId}`, () => undefined, { method: "DELETE" });
}

/** The download URL: still the API's own route, still behind the token. */
export function downloadUrl(attachmentId: string): string {
  return `/api/attachments/${attachmentId}/download`;
}

/** Carry `uploaded_by` for the audit view without pretending to know who that was. */
export interface AttachmentMetadata {
  id: string;
  filename: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedBy: string;
  createdAt: string;
}

export async function getAttachmentMetadata(
  token: string,
  attachmentId: string,
  signal?: AbortSignal,
): Promise<AttachmentMetadata> {
  return apiJson(
    token,
    `/attachments/${attachmentId}`,
    (raw) => {
      const body = assertRecord(raw);
      return {
        id: str(body.id),
        filename: str(body.filename),
        mimeType: str(body.mime_type),
        fileSizeBytes: num(body.file_size_bytes),
        uploadedBy: str(body.uploaded_by),
        createdAt: optStr(body.created_at) ?? "",
      };
    },
    { signal },
  );
}
